import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { useEffect, useState } from "react";

interface Props {
    value: string;
    onChange: (value: string) => void;
    validationError?: string;
    clearFieldError?: (field: string) => void;
}

// Função para extrair o ID do vídeo do YouTube
const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
            return match[1];
        }
    }

    return null;
};

// Função para detectar se é um link do YouTube
const isYouTubeUrl = (url: string): boolean => {
    return /youtube\.com|youtu\.be/.test(url);
};

export const VideoPreview: React.FC<Props> = ({
    value,
    onChange,
    validationError,
    clearFieldError
}) => {
    const [videoId, setVideoId] = useState<string | null>(null);
    const [isYouTube, setIsYouTube] = useState(false);

    useEffect(() => {
        if (value && isYouTubeUrl(value)) {
            const id = extractYouTubeVideoId(value);
            setVideoId(id);
            setIsYouTube(true);
        } else {
            setVideoId(null);
            setIsYouTube(false);
        }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue);
        clearFieldError?.('value');
    };

    return (
        <div className="space-y-4">
            <div>
                <Label htmlFor="video-url" className="text-white">
                    URL do Vídeo *
                </Label>
                <Input
                    id="video-url"
                    value={value}
                    onChange={handleInputChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="bg-[#222222] border-[#333333] text-white placeholder:text-gray-400"
                />
                {validationError && (
                    <p className="text-red-500 text-sm mt-1">{validationError}</p>
                )}
            </div>

            {isYouTube && videoId && (
                <div className="space-y-2">
                    <Label className="text-white">Preview do Vídeo</Label>
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&iv_load_policy=3`}
                            title="YouTube video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                        />
                    </div>
                    <p className="text-xs text-gray-400">
                        Preview do vídeo do YouTube
                    </p>
                </div>
            )}

            {value && !isYouTube && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                        ⚠️ Preview não disponível para este tipo de link. Apenas links do YouTube são suportados no momento.
                    </p>
                </div>
            )}
        </div>
    );
};
